import api from '../api/axios';

// ── EHCP / SEND Code of Practice domains ─────────────────────────────────────
// The four primary areas of need. EHCP tag slugs are `${domainKey}.${subdomain}`
// (see tdp-api enrichmentTaxonomy.ts EHCP_DOMAINS), so the slug prefix maps a
// sub-domain back to its parent area. Stable vocabulary — safe to hardcode.

export type EhcpIndication = 'strength' | 'emerging' | 'need';

export interface SendDomain {
  key: string;        // slug prefix, e.g. 'semh'
  label: string;      // full SEND CoP area name
  short: string;      // compact label for radar axes / tight charts
  colour: string;
}

export const SEND_DOMAINS: SendDomain[] = [
  { key: 'communication-interaction', label: 'Communication & Interaction', short: 'Communication', colour: '#3f7fa3' },
  { key: 'cognition-learning',        label: 'Cognition & Learning',        short: 'Cognition',     colour: '#7cb342' },
  { key: 'semh',                      label: 'Social, Emotional & Mental Health', short: 'SEMH',     colour: '#a06bce' },
  { key: 'sensory-physical',          label: 'Sensory & Physical',          short: 'Sensory/Physical', colour: '#e8a92e' },
];

const DOMAIN_BY_KEY: Record<string, SendDomain> = SEND_DOMAINS.reduce(
  (acc, d) => { acc[d.key] = d; return acc; }, {} as Record<string, SendDomain>,
);

/** Map an EHCP tag slug (e.g. 'semh.emotional-regulation') to its SEND domain. */
export function domainForSlug(slug: string): SendDomain | undefined {
  const key = slug.split('.')[0];
  return DOMAIN_BY_KEY[key];
}

// Indication ordering + colours, reused across the indication charts. The
// progression story reads strength → emerging → need, so order them that way.
export const INDICATIONS: EhcpIndication[] = ['strength', 'emerging', 'need'];
export const INDICATION_LABELS: Record<EhcpIndication, string> = {
  strength: 'Strength',
  emerging: 'Emerging',
  need: 'Area of need',
};
export const INDICATION_COLOURS: Record<EhcpIndication, string> = {
  strength: '#26923f', // brand green — established
  emerging: '#e8a92e', // ochre — developing
  need: '#b8302f',     // clay/red — priority
};

// ── /posts/ai/summary response ───────────────────────────────────────────────

export interface EhcpCountRow {
  area: string;                 // full sub-domain slug
  indication: EhcpIndication;
  count: number;
}

export interface EhcpWeekRow extends EhcpCountRow {
  week_start: string;           // YYYY-MM-DD (Monday)
}

export interface TagCountRow {
  tag: string;
  count: number;
}

export interface PostsWeekRow {
  week_start: string;           // YYYY-MM-DD (Monday)
  posts: number;                // in-scope (published) posts that week
}

export interface InsightsSummary {
  posts_considered: number;
  ehcp: EhcpCountRow[];
  safeguarding: TagCountRow[];
  staff_action: TagCountRow[];
  ehcp_by_week?: EhcpWeekRow[];   // present only when fetched with bucket=week
  posts_by_week?: PostsWeekRow[]; // present only when fetched with bucket=week
}

// ── ABC behaviour (Behaviour Insights tab) ───────────────────────────────────
// The four FBA functions, in a FIXED categorical order (never cycled). Colours are
// the validated default categorical slots 1–4 (dataviz skill — passes the CVD +
// normal-vision gates on the light chart surface; the sub-3:1 contrast on aqua/
// yellow is covered by the relief rule: the function bars carry direct labels).
export type AbcFunction = 'escape' | 'attention' | 'tangible' | 'sensory';
export const ABC_FUNCTIONS: AbcFunction[] = ['escape', 'attention', 'tangible', 'sensory'];
export const ABC_FUNCTION_LABELS: Record<AbcFunction, string> = {
  escape: 'Escape', attention: 'Attention', tangible: 'Tangible', sensory: 'Sensory',
};
export const ABC_FUNCTION_COLOURS: Record<AbcFunction, string> = {
  escape: '#2a78d6', attention: '#eb6834', tangible: '#1baf7a', sensory: '#eda100',
};
// '' function/trigger → the neutral "unclassified" bucket (folds to grey, not a hue).
export const ABC_UNCLASSIFIED_COLOUR = '#898781';
export const ABC_UNCLASSIFIED_LABEL = 'Unclassified';
// Single-series colour for the episode-frequency trend (the app's chart green;
// deliberately neutral since the valence filter is switchable).
export const ABC_FREQUENCY_COLOUR = '#26923f';

export type AbcValenceFilter = 'negative' | 'positive' | 'all';

export interface AbcBucketRow { bucket_start: string; episodes: number; }
export interface AbcFunctionRow { fn: string; count: number; }              // '' = unclassified
export interface AbcFunctionBucketRow { bucket_start: string; fn: string; count: number; }
export interface AbcAntecedentRow { antecedent: string; count: number; }    // '' = untagged
export interface AbcTagCountRow { tag: string; count: number; }
export interface AbcEvidenceRow {
  post_id: number;
  post_date: string;
  antecedent: string;
  behavior: string;
  consequence: string;
  valence: string;
  function: string;       // AbcFunction | ''
  antecedent_tag: string; // trigger slug | ''
}

export interface AbcSummary {
  valence: AbcValenceFilter;
  bucket: 'week' | 'month';
  episodes_by_bucket: AbcBucketRow[];
  function_distribution: AbcFunctionRow[];
  function_by_bucket: AbcFunctionBucketRow[];
  antecedent_distribution: AbcAntecedentRow[];
  staff_action_cooccurrence: AbcTagCountRow[];
  evidence: AbcEvidenceRow[];
}

/**
 * Child-focused AI insights, backed by the org/child/date-scoped aggregation in
 * GET /posts/ai/summary (counts only — no post text, so no PII concern).
 * Pass weekly=true to also receive the week-bucketed EHCP series for timelines.
 */
export class InsightsDataService {
  static async fetchSummary(
    childId: number,
    fromDate: string,
    toDate: string,
    opts: { weekly?: boolean } = {},
  ): Promise<InsightsSummary> {
    const params: Record<string, string> = {
      childId: String(childId),
      fromDate,
      toDate,
    };
    if (opts.weekly) params.bucket = 'week';
    const response = await api.get('/posts/ai/summary', { params });
    return response.data;
  }

  // ABC behaviour aggregation (Behaviour Insights tab). Returns counts + a capped,
  // desanitised evidence list — so, unlike fetchSummary, the response carries post
  // text (real names). Role-gated server-side identically.
  static async fetchAbcSummary(
    childId: number,
    fromDate: string,
    toDate: string,
    opts: { valence: AbcValenceFilter; bucket: 'week' | 'month' },
  ): Promise<AbcSummary> {
    const params: Record<string, string> = {
      childId: String(childId),
      fromDate,
      toDate,
      valence: opts.valence,
      bucket: opts.bucket,
    };
    const response = await api.get('/posts/ai/abc-summary', { params });
    return response.data;
  }
}
